import { useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../lib/api.js";

/**
 * Forgot-password page — email form that triggers a reset email.
 *
 * Always shows "check your inbox" on success regardless of whether the
 * email exists (the API always returns 200 for the same reason — no
 * user enumeration).
 */
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await apiFetch("/api/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      setSubmitted(true);
    } catch (err) {
      // Only surface real errors (validation, rate limit) — the "success
      // regardless" policy means we never see a distinct failure for
      // nonexistent email.
      setError(err.message || "Request failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell
      title={submitted ? "Check your inbox" : "Reset your password"}
      subtitle={submitted
        ? "If we have an account for that email, a reset link is on its way. The link is valid for 1 hour."
        : "Enter the email you sign in with. We'll send you a link to set a new password."
      }
    >
      {!submitted && (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <Field
            label="Email" type="email" value={email}
            onChange={setEmail} autoFocus required autoComplete="email"
          />
          {error && <ErrorBanner>{error}</ErrorBanner>}
          <button
            type="submit" disabled={loading}
            className="w-full h-10 rounded bg-accent text-white text-[13px] font-semibold disabled:opacity-60 hover:opacity-90 transition-opacity"
          >
            {loading ? "Sending…" : "Send reset link"}
          </button>
        </form>
      )}
      <div className="mt-5 text-center">
        <Link to="/login" className="text-[12px] text-text-secondary hover:text-text-primary">
          ← Back to sign in
        </Link>
      </div>
    </AuthShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Shared primitives for the three public auth pages.
// ─────────────────────────────────────────────────────────────────────────

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-app-bg flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-8 h-8 rounded bg-accent flex items-center justify-center text-white text-[14px] font-bold">P</div>
          <span className="text-[18px] font-semibold text-text-primary">Prism</span>
        </div>
        <div className="bg-surface border border-border-default rounded-card p-6">
          <div className="mb-5">
            <h1 className="text-[15px] font-semibold text-text-primary mb-1">{title}</h1>
            {subtitle && <p className="text-[12px] text-text-secondary leading-snug">{subtitle}</p>}
          </div>
          {children}
        </div>
        <p className="text-[11px] text-text-muted text-center mt-5">
          Managed by <span className="font-medium text-text-secondary">Bayshore Marketing Group</span>
        </p>
      </div>
    </div>
  );
}

export function Field({ label, type = "text", value, onChange, autoFocus, autoComplete, required, placeholder, help }) {
  return (
    <div>
      <label
        className="block text-[11px] uppercase text-text-muted font-medium mb-1.5"
        style={{ letterSpacing: "0.5px" }}
      >
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        required={required}
        placeholder={placeholder}
        className="w-full h-10 px-3 rounded border border-border-default text-[13px] text-text-primary bg-surface focus:outline-none focus:border-accent transition-colors"
      />
      {help && <div className="text-[10px] text-text-muted mt-1">{help}</div>}
    </div>
  );
}

export function ErrorBanner({ children }) {
  return (
    <div role="alert" className="text-[12px] text-negative rounded px-3 py-2 border" style={{ backgroundColor: "#fef2f2", borderColor: "#fecaca" }}>
      {children}
    </div>
  );
}
